/**
 * modes/mode-router.js
 * Điều phối chuyển đổi giữa các chế độ sân khấu (Studio, Animals, Walker, Lab, City, Drama).
 */

import { resetStage } from './stage-reset.js';
import { fitShadowCamera, applyLightingPreset } from '../lighting-presets.js';
import { updateViewOffset } from '../scene-setup.js';
import { scrollActiveSceneTabIntoView } from '../ui/navigation-ui.js';

export const MODE_DEFAULT_PANEL = {
  studio: 'customizer',
  animals: 'animals',
  walker: 'interaction',
  lab: 'interaction',
  city: 'characters',
  drama: null
};

export function switchMode(mode, state, handlers = {}, options = {}) {
  // 1. Reset sân khấu tập trung, dọn dẹp các đối tượng của chế độ cũ
  resetStage(state, handlers);

  state.currentMode = mode;
  document.querySelectorAll('.scene-tab').forEach((tab) => {
    const isActive = tab.dataset.mode === mode;
    tab.classList.toggle('active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  scrollActiveSceneTabIntoView();

  // Đồng bộ trạng thái active của nút panel animals (#nav-btn-animals)
  const navBtnAnimals = document.getElementById('nav-btn-animals');
  if (navBtnAnimals) {
    navBtnAnimals.classList.toggle('active', mode === 'animals');
  }

  // Nếu rời khỏi chế độ kịch bản, dừng drama director và dọn dẹp UI
  if (mode !== 'drama') {
    if (state.dramaDirector) {
      state.dramaDirector.pause();
      state.dramaDirector.stopTts();
      if (state.dramaDirector.group && state.dramaDirector.group.parent) {
        state.scene.remove(state.dramaDirector.group);
      }
    }
    const subtitleBox = document.getElementById('drama-subtitle-box');
    if (subtitleBox) subtitleBox.style.display = 'none';
    const playbackDock = document.getElementById('drama-playback-dock');
    if (playbackDock) playbackDock.style.display = 'none';

    const panel = document.querySelector('.control-panel');
    if (panel) panel.style.display = 'flex';
    const hints = document.querySelector('.viewport-hints');
    if (hints) hints.style.display = 'flex';
    const reopenBtn = document.getElementById('btn-reopen-hints');
    if (reopenBtn) {
      reopenBtn.style.display = hints?.classList.contains('viewport-hints--hidden') ? 'inline-flex' : 'none';
    }
    if (typeof handlers.updateMobileFabVisibility === 'function') handlers.updateMobileFabVisibility();
  } else {
    if (typeof handlers.updateMobileFabVisibility === 'function') handlers.updateMobileFabVisibility();
  }

  // Thiết lập từng chế độ
  if (mode === 'studio') {
    if (state.studioEnvironmentGroup) state.studioEnvironmentGroup.visible = true;
    if (state.pedestalGroup) state.pedestalGroup.visible = true;
    if (state.gridHelper) state.gridHelper.visible = false;

    if (state.camera && state.controls) {
      state.camera.position.set(0, 1.8, 4.8);
      state.controls.target.set(0, 1.1, 0);
    }

    if (typeof handlers.setupStudioAvatar === 'function') {
      handlers.setupStudioAvatar(state.currentPresetKey);
    }
  } else if (mode === 'animals') {
    if (typeof handlers.setupAnimalsMode === 'function') handlers.setupAnimalsMode(state);
  } else if (mode === 'walker') {
    if (typeof handlers.setupWalkerMode === 'function') handlers.setupWalkerMode(state);
  } else if (mode === 'lab') {
    if (typeof handlers.setupLabMode === 'function') handlers.setupLabMode(state);
  } else if (mode === 'city') {
    if (typeof handlers.setupVietnamCityMode === 'function') handlers.setupVietnamCityMode(false, state);
  } else if (mode === 'drama') {
    if (typeof handlers.setupDramaMode === 'function') handlers.setupDramaMode(state);
  }

  // Cập nhật shadow camera và ánh sáng / bầu trời theo chế độ
  if (mode === 'city' || mode === 'drama') {
    fitShadowCamera(state, { center: [0, 0, 0], halfSize: 22, near: 1, far: 75, mapSize: 1536 });
  } else {
    fitShadowCamera(state, { center: [0, 1, 0], halfSize: 5, mapSize: 2048 });
  }
  applyLightingPreset(state, state.currentProject?.environment?.timeOfDay || 'noon');

  // Điều hướng panel theo ngữ cảnh (nếu không đặt keepPanel: true)
  if (!options.keepPanel && typeof handlers.switchStudioPanel === 'function') {
    const targetPanel = MODE_DEFAULT_PANEL[mode];
    if (targetPanel) {
      handlers.switchStudioPanel(targetPanel);
    }
  }

  // Cập nhật view offset để né panel hoặc đưa về tâm (drama / mobile)
  updateViewOffset(state);
}

export function initSceneTabListeners(state, switchModeFn) {
  document.querySelectorAll('.scene-tab').forEach((tab) => {
    tab.addEventListener('click', () => switchModeFn(tab.dataset.mode));
  });

  // Tự động ngắt giọng đọc TTS khi ẩn hoặc chuyển tab trình duyệt
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && state.dramaDirector) {
      state.dramaDirector.stopTts();
    }
  });
}
